import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useBetaData } from '../../contexts/BetaDataContext';
import { useToast } from '../common/Toast';
import { supabase } from '../../supabaseClient';
import CustomSelect from '../common/CustomSelect';
import { WORK_ITEM_TYPE_LABELS } from '../../constants/workItem';
import './ExportModal.scss';

const STATUS_OPTIONS = [
  { value: 'all', label: '全部' },
  { value: 'incomplete', label: '未完成' },
  { value: 'completed', label: '已完成' },
];

const FORMAT_OPTIONS = [
  { value: 'tsv', label: 'TSV（貼 Excel）' },
  { value: 'json', label: 'JSON（程式處理）' },
];

// ─── 可搜尋多選下拉（專案選擇器）──────────
const ProjectMultiSelect = ({ options, selected, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()))
    : options;

  const triggerLabel = selected.length === 0
    ? '選擇專案...'
    : `已選 ${selected.length} 個專案`;

  return (
    <div className={`project-multi-select ${open ? 'open' : ''}`} ref={ref}>
      <button type="button" className="multi-trigger" onClick={() => setOpen(!open)}>
        <span className={selected.length === 0 ? 'placeholder' : ''}>{triggerLabel}</span>
        <svg className="select-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="multi-dropdown">
          <div className="multi-search">
            <input
              ref={searchRef}
              type="text"
              placeholder="搜尋專案..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {selected.length > 0 && (
            <button type="button" className="multi-clear" onClick={onClear}>
              清除全部
            </button>
          )}
          <div className="multi-list">
            {filtered.length === 0 ? (
              <div className="multi-empty">找不到符合的專案</div>
            ) : (
              filtered.map((opt) => (
                <label key={opt.value} className="multi-option">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => onToggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ExportModal = ({ isOpen, onClose }) => {
  const { profile } = useAuth();
  const { projects, statusOptions } = useBetaData();
  const toast = useToast();

  // 匯出設定
  const [exportWorkItems, setExportWorkItems] = useState(true);
  const [exportItems, setExportItems] = useState(false);
  const [exportDeliverables, setExportDeliverables] = useState(false);
  const [scopeType, setScopeType] = useState('all'); // 'all' | 'projects'
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [timeRange, setTimeRange] = useState('all'); // 'all' | 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [format, setFormat] = useState('tsv');
  const [exporting, setExporting] = useState(false);

  const projectOptions = useMemo(() =>
    (projects || []).map((p) => ({ value: p.id, label: p.name })),
    [projects]
  );

  const toggleProject = (projectId) => {
    setSelectedProjectIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  const statusLabelOf = (v) => (statusOptions || []).find((s) => s.value === v)?.label || v || '';

  const handleExport = async () => {
    if (!exportWorkItems && !exportItems && !exportDeliverables) {
      toast.error('請至少選擇一種匯出內容');
      return;
    }
    setExporting(true);

    try {
      let workItemData = [];
      let itemsData = [];
      let deliverableData = [];

      // ─── 查 工作項目(新版,我被指派的父/子項)───
      if (exportWorkItems) {
        let query = supabase
          .from('work_items')
          .select(`
            id, work_item_no, title, description, status, priority, type,
            start_date, end_date, created_at, completed_at, parent_id,
            projects:project_id (id, name),
            assignees:work_item_assignees!inner (user_id)
          `)
          .eq('assignees.user_id', profile.id)
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        // 狀態篩選(以 completed_at 判定完成與否,不綁特定 status enum)
        if (statusFilter === 'completed') {
          query = query.not('completed_at', 'is', null);
        } else if (statusFilter === 'incomplete') {
          query = query.is('completed_at', null);
        }

        // 時間範圍
        if (timeRange === 'custom') {
          if (startDate) query = query.gte('created_at', startDate);
          if (endDate) query = query.lte('created_at', endDate + 'T23:59:59');
        }

        const { data, error } = await query;
        if (error) throw error;

        workItemData = (data || [])
          .filter((wi) => {
            if (scopeType === 'projects' && selectedProjectIds.length > 0) {
              const projectId = wi.projects?.id;
              return projectId && selectedProjectIds.includes(projectId);
            }
            return true;
          })
          .map((wi) => ({ ...wi, _statusLabel: statusLabelOf(wi.status) }));
      }

      // ─── 查 Items (ToDo) ───
      if (exportItems) {
        let query = supabase
          .from('items')
          .select(`
            id, title, description, status, priority,
            start_date, end_date, created_at, completed_at, completed,
            tickets:ticket_id (
              id, title, ticket_no,
              projects:project_id (id, name)
            )
          `)
          .eq('assignee_id', profile.id)
          .order('created_at', { ascending: false });

        // 狀態篩選
        if (statusFilter === 'completed') {
          query = query.eq('completed', true);
        } else if (statusFilter === 'incomplete') {
          query = query.eq('completed', false);
        }

        // 時間範圍
        if (timeRange === 'custom') {
          if (startDate) query = query.gte('created_at', startDate);
          if (endDate) query = query.lte('created_at', endDate + 'T23:59:59');
        }

        const { data, error } = await query;
        if (error) throw error;

        // 專案篩選（client-side，因為 Supabase 不支援 nested filter）
        itemsData = (data || []).filter((item) => {
          if (scopeType === 'projects' && selectedProjectIds.length > 0) {
            const projectId = item.tickets?.projects?.id;
            return projectId && selectedProjectIds.includes(projectId);
          }
          return true;
        });
      }

      // ─── 查 Deliverable Items ───
      if (exportDeliverables) {
        let query = supabase
          .from('deliverable_items')
          .select(`
            id, title, notes, role_type, estimated_hours, actual_hours,
            is_completed, completed_at, created_at,
            deliverables:deliverable_id (
              id, title,
              phases:phase_id (
                id, name,
                projects:project_id (id, name)
              )
            )
          `)
          .eq('assignee_id', profile.id)
          .order('created_at', { ascending: false });

        // 狀態篩選
        if (statusFilter === 'completed') {
          query = query.eq('is_completed', true);
        } else if (statusFilter === 'incomplete') {
          query = query.eq('is_completed', false);
        }

        // 時間範圍
        if (timeRange === 'custom') {
          if (startDate) query = query.gte('created_at', startDate);
          if (endDate) query = query.lte('created_at', endDate + 'T23:59:59');
        }

        const { data, error } = await query;
        if (error) throw error;

        // 專案篩選
        deliverableData = (data || []).filter((item) => {
          if (scopeType === 'projects' && selectedProjectIds.length > 0) {
            const projectId = item.deliverables?.phases?.projects?.id;
            return projectId && selectedProjectIds.includes(projectId);
          }
          return true;
        });
      }

      // ─── 組裝輸出 ───
      const totalCount = workItemData.length + itemsData.length + deliverableData.length;
      if (totalCount === 0) {
        toast.info('沒有符合條件的資料');
        setExporting(false);
        return;
      }

      let output;
      if (format === 'tsv') {
        output = buildTsv(itemsData, deliverableData, workItemData);
      } else {
        output = buildJson(itemsData, deliverableData, workItemData);
      }

      await navigator.clipboard.writeText(output);
      toast.success(`已複製 ${totalCount} 筆資料到剪貼簿`);
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      toast.error('匯出失敗：' + (err.message || '未知錯誤'));
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="export-modal">
        {/* Header */}
        <div className="modal-header">
          <h2>匯出我的資料</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* 匯出內容 */}
          <div className="form-section">
            <label className="section-label">匯出內容</label>
            <div className="checkbox-group">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={exportWorkItems}
                  onChange={(e) => setExportWorkItems(e.target.checked)}
                />
                <span>工作項目（新版）</span>
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={exportItems}
                  onChange={(e) => setExportItems(e.target.checked)}
                />
                <span>Ticket 內 ToDo (Items)（舊版）</span>
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={exportDeliverables}
                  onChange={(e) => setExportDeliverables(e.target.checked)}
                />
                <span>Phase 內工作項目（Deliverables）（舊版）</span>
              </label>
            </div>
          </div>

          {/* 匯出範圍 */}
          <div className="form-section">
            <label className="section-label">匯出範圍</label>
            <div className="radio-group">
              <label className="radio-item">
                <input
                  type="radio"
                  name="scope"
                  checked={scopeType === 'all'}
                  onChange={() => setScopeType('all')}
                />
                <span>全部專案</span>
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="scope"
                  checked={scopeType === 'projects'}
                  onChange={() => setScopeType('projects')}
                />
                <span>指定專案</span>
              </label>
            </div>
            {scopeType === 'projects' && (
              <ProjectMultiSelect
                options={projectOptions}
                selected={selectedProjectIds}
                onToggle={toggleProject}
                onClear={() => setSelectedProjectIds([])}
              />
            )}
          </div>

          {/* 時間範圍 */}
          <div className="form-section">
            <label className="section-label">時間範圍（依建立日期）</label>
            <div className="radio-group">
              <label className="radio-item">
                <input
                  type="radio"
                  name="timeRange"
                  checked={timeRange === 'all'}
                  onChange={() => setTimeRange('all')}
                />
                <span>不限</span>
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="timeRange"
                  checked={timeRange === 'custom'}
                  onChange={() => setTimeRange('custom')}
                />
                <span>自訂</span>
              </label>
            </div>
            {timeRange === 'custom' && (
              <div className="date-range">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span className="date-separator">～</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* 狀態篩選 */}
          <div className="form-section">
            <label className="section-label">狀態篩選</label>
            <CustomSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_OPTIONS}
              portal
            />
          </div>

          {/* 匯出格式 */}
          <div className="form-section">
            <label className="section-label">匯出格式</label>
            <div className="radio-group">
              {FORMAT_OPTIONS.map((opt) => (
                <label key={opt.value} className="radio-item">
                  <input
                    type="radio"
                    name="format"
                    checked={format === opt.value}
                    onChange={() => setFormat(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>取消</button>
          <button
            className="btn-primary"
            onClick={handleExport}
            disabled={exporting || (!exportWorkItems && !exportItems && !exportDeliverables)}
          >
            {exporting ? '匯出中...' : '複製到剪貼簿'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── 輸出格式組裝 ──────────────────────

const STATUS_MAP = {
  pending: '待處理',
  in_progress: '進行中',
  completed: '已完成',
  closed: '已結案',
};

const PRIORITY_MAP = {
  0: '無',
  1: '低',
  2: '中',
  3: '高',
  4: '緊急',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  return dateStr.slice(0, 10);
}

function buildTsv(items, deliverables, workItems = []) {
  const lines = [];

  if (workItems.length > 0) {
    lines.push(['類型', '專案', '編號', '層級', '標題', '說明', '項目類型', '狀態', '優先度', '開始日期', '結束日期', '建立日期', '完成日期'].join('\t'));
    workItems.forEach((wi) => {
      lines.push([
        '工作項目',
        wi.projects?.name || '',
        wi.work_item_no != null ? `#${wi.work_item_no}` : '',
        wi.parent_id ? '子項' : '工作項目',
        wi.title || '',
        (wi.description || '').replace(/[\t\n\r]/g, ' '),
        WORK_ITEM_TYPE_LABELS[wi.type] || wi.type || '',
        wi._statusLabel || '',
        PRIORITY_MAP[wi.priority] ?? String(wi.priority ?? ''),
        formatDate(wi.start_date),
        formatDate(wi.end_date),
        formatDate(wi.created_at),
        formatDate(wi.completed_at),
      ].join('\t'));
    });
  }

  if (items.length > 0) {
    if (lines.length > 0) lines.push('');
    // Items header
    lines.push(['類型', '專案', 'Ticket', '標題', '說明', '狀態', '優先度', '開始日期', '結束日期', '建立日期', '完成日期'].join('\t'));
    items.forEach((item) => {
      const project = item.tickets?.projects?.name || '';
      const ticketTitle = item.tickets
        ? `${item.tickets.title} (#${item.tickets.ticket_no})`
        : '';
      lines.push([
        'ToDo',
        project,
        ticketTitle,
        item.title || '',
        (item.description || '').replace(/[\t\n\r]/g, ' '),
        STATUS_MAP[item.status] || item.status || '',
        PRIORITY_MAP[item.priority] ?? String(item.priority ?? ''),
        formatDate(item.start_date),
        formatDate(item.end_date),
        formatDate(item.created_at),
        formatDate(item.completed_at),
      ].join('\t'));
    });
  }

  if (deliverables.length > 0) {
    if (lines.length > 0) lines.push(''); // 空行分隔
    // Deliverables header
    lines.push(['類型', '專案', 'Phase', '頁面', '角色', '標題', '備註', '預估工時', '實際工時', '完成', '完成日期'].join('\t'));
    deliverables.forEach((item) => {
      const phase = item.deliverables?.phases;
      const project = phase?.projects?.name || '';
      const phaseName = phase?.name || '';
      const pageName = item.deliverables?.title || '';
      lines.push([
        'Deliverable',
        project,
        phaseName,
        pageName,
        item.role_type || '',
        item.title || '',
        (item.notes || '').replace(/[\t\n\r]/g, ' '),
        item.estimated_hours != null ? String(item.estimated_hours) : '',
        item.actual_hours != null ? String(item.actual_hours) : '',
        item.is_completed ? '是' : '否',
        formatDate(item.completed_at),
      ].join('\t'));
    });
  }

  return lines.join('\n');
}

function buildJson(items, deliverables, workItems = []) {
  const result = {};

  if (workItems.length > 0) {
    result.work_items = workItems.map((wi) => ({
      專案: wi.projects?.name || '',
      編號: wi.work_item_no != null ? `#${wi.work_item_no}` : '',
      層級: wi.parent_id ? '子項' : '工作項目',
      標題: wi.title || '',
      說明: wi.description || '',
      項目類型: WORK_ITEM_TYPE_LABELS[wi.type] || wi.type || '',
      狀態: wi._statusLabel || '',
      優先度: PRIORITY_MAP[wi.priority] ?? String(wi.priority ?? ''),
      開始日期: formatDate(wi.start_date),
      結束日期: formatDate(wi.end_date),
      建立日期: formatDate(wi.created_at),
      完成日期: formatDate(wi.completed_at),
    }));
  }

  if (items.length > 0) {
    result.items = items.map((item) => ({
      專案: item.tickets?.projects?.name || '',
      Ticket: item.tickets
        ? `${item.tickets.title} (#${item.tickets.ticket_no})`
        : '',
      標題: item.title || '',
      說明: item.description || '',
      狀態: STATUS_MAP[item.status] || item.status || '',
      優先度: PRIORITY_MAP[item.priority] ?? String(item.priority ?? ''),
      開始日期: formatDate(item.start_date),
      結束日期: formatDate(item.end_date),
      建立日期: formatDate(item.created_at),
      完成日期: formatDate(item.completed_at),
    }));
  }

  if (deliverables.length > 0) {
    result.deliverable_items = deliverables.map((item) => {
      const phase = item.deliverables?.phases;
      return {
        專案: phase?.projects?.name || '',
        Phase: phase?.name || '',
        頁面: item.deliverables?.title || '',
        角色: item.role_type || '',
        標題: item.title || '',
        備註: item.notes || '',
        預估工時: item.estimated_hours ?? '',
        實際工時: item.actual_hours ?? '',
        完成: item.is_completed ? '是' : '否',
        完成日期: formatDate(item.completed_at),
      };
    });
  }

  return JSON.stringify(result, null, 2);
}

export default ExportModal;
